import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";

const SECTIONS = [
    {
        n: "01",
        h: "Agreement",
        p: [
            "These Terms govern your use of the Ventara website at iventara.com and any related services we offer through it (\"the Service\"). By using the Service, you agree to these Terms.",
            "If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization, and \"you\" refers to both you and the organization.",
        ],
    },
    {
        n: "02",
        h: "The Service",
        p: [
            "Ventara publishes information about its products, research, and open positions, invites conversations through contact forms and applications, and distributes an occasional newsletter. The Service is provided as-is; features may evolve.",
            "Access to individual product platforms (for example, Sonare, Atlas, Foreground, Lumen, or Compass) is subject to a separate written agreement with Ventara.",
        ],
    },
    {
        n: "03",
        h: "Eligibility",
        p: [
            "You must be at least sixteen years old, or the age of digital majority in your jurisdiction if higher, to use the Service.",
            "You are responsible for the accuracy of information you provide, including in contact submissions and applications.",
        ],
    },
    {
        n: "04",
        h: "Acceptable use",
        p: [
            "You agree not to:",
            "Access, tamper with, or use non-public areas of the Service, our infrastructure, or our security systems.",
            "Probe, scan, or test the vulnerability of any system without prior written permission.",
            "Interfere with or disrupt the Service, including by transmitting malware or by any denial-of-service attack.",
            "Scrape or copy content in a manner that violates the Service's technical restrictions or the intellectual property rights described below.",
            "Submit false or misleading information, impersonate any person or entity, or submit content that is unlawful, defamatory, or infringing.",
        ],
    },
    {
        n: "05",
        h: "Intellectual property",
        p: [
            "The Service — including its text, design, source code, illustrations, and the Ventara brand — is owned by Ventara or its licensors and is protected by copyright, trademark, and other laws.",
            "Nothing in these Terms transfers ownership of any Ventara intellectual property to you. Personal, non-commercial reading and sharing of publicly posted articles with attribution is permitted.",
            "\"Ventara\", the Ventara mark, and the names of Ventara products are trademarks of Ventara. Please do not use them in a way that suggests sponsorship or endorsement without our written permission.",
        ],
    },
    {
        n: "06",
        h: "Your content",
        p: [
            "When you submit content to us — through a contact form, an application, or otherwise — you retain ownership of it, and you grant us a worldwide, royalty-free licence to use it to respond to and evaluate your submission.",
            "You represent that you have the right to submit the content, that it does not violate any third-party rights, and that it does not include the confidential information of others without permission.",
        ],
    },
    {
        n: "07",
        h: "Third-party services",
        p: [
            "The Service may link to or integrate with services we do not operate. We do not endorse and are not responsible for those services or the information they present, and your use of them is governed by their own terms.",
        ],
    },
    {
        n: "08",
        h: "Disclaimers",
        p: [
            "The Service is provided \"as is\" and \"as available\" without warranties of any kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.",
            "We do not warrant that the Service will be uninterrupted or error-free, that defects will be corrected, or that the Service is free of viruses or other harmful components.",
        ],
    },
    {
        n: "09",
        h: "Limitation of liability",
        p: [
            "To the fullest extent permitted by law, Ventara and its affiliates, officers, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenues, data, or goodwill, arising out of or in connection with your use of the Service.",
            "In no event will Ventara's aggregate liability for any claim arising out of or in connection with the Service exceed one hundred United States dollars (US $100).",
            "Some jurisdictions do not allow the exclusion or limitation of certain damages; in those jurisdictions, our liability will be limited to the greatest extent permitted by law.",
        ],
    },
    {
        n: "10",
        h: "Indemnification",
        p: [
            "You agree to indemnify and hold harmless Ventara and its affiliates from any claim, demand, loss, or expense (including reasonable legal fees) arising out of your breach of these Terms, your misuse of the Service, or your violation of any law or the rights of any third party.",
        ],
    },
    {
        n: "11",
        h: "Termination",
        p: [
            "We may suspend or terminate your access to the Service at any time, without notice, if we believe you have violated these Terms or if your use poses a risk to us, to other users, or to third parties.",
            "You may stop using the Service at any time.",
        ],
    },
    {
        n: "12",
        h: "Changes",
        p: [
            "We may modify these Terms from time to time. If we make material changes, we will update the effective date at the top of this page. Your continued use of the Service after the change becomes effective constitutes your acceptance of the revised Terms.",
        ],
    },
    {
        n: "13",
        h: "Governing law",
        p: [
            "These Terms are governed by the laws of the jurisdiction in which Ventara is incorporated, without regard to conflict-of-law principles. Disputes arising from these Terms will be resolved in the competent courts of that jurisdiction, except where mandatory local law provides otherwise.",
        ],
    },
    {
        n: "14",
        h: "Contact",
        p: [
            "Legal questions go to legal@iventara.com. General inquiries, hello@iventara.com.",
        ],
    },
];

export default function Terms() {
    return (
        <Layout title="Terms — Ventara" description="Terms of use for the Ventara website.">
            <PageHero
                eyebrow="Legal · Effective February 2026"
                title={<>The terms<br />in plain language.</>}
                lead="These are the terms you agree to when you use iventara.com. They are meant to be read — not skimmed — so we have kept them short."
            />
            <Section className="pb-32">
                <div className="divide-y divide-border border-t border-b border-border max-w-4xl">
                    {SECTIONS.map((s) => (
                        <Reveal key={s.n}>
                            <div className="grid lg:grid-cols-12 gap-8 py-10" data-testid={`terms-${s.n}`}>
                                <div className="lg:col-span-2 label pt-2">{s.n}</div>
                                <div className="lg:col-span-10">
                                    <h2 className="font-serif text-3xl tracking-tight">{s.h}</h2>
                                    <div className="prose-body mt-4 space-y-3 max-w-2xl">
                                        {s.p.map((line, i) => (<p key={i}>{line}</p>))}
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>
        </Layout>
    );
}
